import {useCallback, useEffect, useState} from 'react';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import withIsConnected from '../../hoc/withIsConnected';
import Localized from 'src/constants/AppStrings';
import {IsConnectedProps} from 'src/types/Screen';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import BaseScreen from 'src/components/screens/BaseScreen';
import {CampusLocationType} from 'src/types/CampusLocationTypes';
import AVText from 'src/components/elements/AVText';
import {View, StyleSheet} from 'react-native';
import Styles from 'src/components/Styles';
import {getDescriber} from 'src/components/screens/sendSnack/descriptor/sendasnack/DescriptorType';
import {MachineType} from 'src/types/MachineType';
import VendingMachineList from 'src/components/elements/vending/VendingMachineList';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';

type ChooseVendingLocationScreenProps = {
  locations: CampusLocationType[];
  onLocationSelection: (machine, autoAdvanced?: boolean) => Promise<void>;
  onNutritionClick: (machine: MachineType) => void;
} & IsConnectedProps;

const ChooseVendingLocationScreen = (
  props: ChooseVendingLocationScreenProps,
) => {
  const [selectedLocation, setSelectedLocation] = useState<number>(0);

  useEffect(() => {
    FirebaseAnalytic.trackEvent(
      'componentDidMount',
      'ChooseVendingLocationScreen',
      {
        ...props,
      },
    );
  }, []);

  const onLocationClick = (index: number) => {
    setSelectedLocation(index);
  };

  const handleOnNutritionClick = useCallback(
    () => props.onNutritionClick(props.locations[selectedLocation]),
    [props.onNutritionClick, selectedLocation],
  );

  const handleOnLocationSelection = useCallback(
    () => props.onLocationSelection(props.locations[selectedLocation]),
    [props.onLocationSelection, selectedLocation],
  );

  return (
    <BaseScreen
      title={Localized.Labels.vending}
      accessibilityHint={`Press to navigate back to the Home screen`}
      accessibilityLabel={'Back arrow'}
    >
      <AVText
        style={[
          styles.instructionTextTitle,
          getDescriber()?.snackDetailsBtnFontSTyle(),
        ]}
      >
        {`${Localized.Labels.instructions}: `}
        <AVText style={styles.instructionText}>
          {Localized.Labels.start_by_choosing_a_vending_description}
        </AVText>
      </AVText>
      <AVText
        style={[styles.vendingText, getDescriber()?.snackDetailsBtnFontSTyle()]}
      >
        {Localized.Labels.vending_machines}
      </AVText>
      <VendingMachineList
        locations={props.locations}
        selectedLocation={selectedLocation}
        isConnected={true}
        onLocationClick={onLocationClick}
      />
      <View style={styles.buttonView}>
        <RoundedAppButton
          titleText={Localized.Buttons.view_nutrition}
          buttonType={RoundedAppButtonType.Outline}
          textStyle={styles.buttonText}
          buttonContainerStyle={styles.button}
          onPress={handleOnNutritionClick}
        />
        <RoundedAppButton
          titleText={Localized.Buttons.start_selection}
          buttonType={RoundedAppButtonType.Solid}
          textStyle={styles.buttonText}
          buttonContainerStyle={styles.button}
          onPress={handleOnLocationSelection}
        />
      </View>
    </BaseScreen>
  );
};

export default withForwardedNavigationParams()(
  withIsConnected(ChooseVendingLocationScreen),
);

const styles = StyleSheet.create({
  instructionTextTitle: {
    fontSize: getLayoutDescriber().bodyFontSize(),
    color: Styles.black,
    marginHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    fontWeight: '700',
    marginTop: Styles.Spacing.m3,
  },
  vendingText: {
    fontSize: getLayoutDescriber().subHeaderFontSize(),
    marginHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    color: Styles.black,
    fontWeight: '700',
    marginTop: Styles.Spacing.m3,
  },
  instructionText: {
    fontWeight: '400',
  },
  buttonView: {
    height: 74,
    flexDirection: 'row',
    paddingTop: Styles.Spacing.m2,
    justifyContent: 'center',
  },
  buttonText: {
    fontSize: getLayoutDescriber().bodyFontSize(),
    fontWeight: '700',
    paddingHorizontal: Styles.Spacing.m1,
    textTransform: 'none',
  },
  button: {
    marginHorizontal: Styles.Spacing.m2,
  },
});
